import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Download, X, Smartphone } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const V = '#7FB342';
const STORAGE_KEY = 'kadmi_pwa_banner_dismissed_at';
const DELAY_BEFORE_SHOW = 22000; // 22 seconds
const REMIND_DELAY_DAYS = 3;

const isMobile = () => typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const isIOS = () => typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true);

const wasRecentlyDismissed = () => {
  try {
    const ts = localStorage.getItem(STORAGE_KEY);
    if (!ts) return false;
    const diffDays = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
    return diffDays < REMIND_DELAY_DAYS;
  } catch { return false; }
};

const PwaInstallBanner = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosInstructions, setShowIosInstructions] = useState(false);

  const tp = isDark ? '#F0EFFF' : '#1A1A2E';
  const ts = isDark ? '#9998B8' : '#6B6B8A';
  const cardBg = isDark ? '#1A1A2E' : '#FFFFFF';
  const bd = isDark ? '#2E2E45' : '#E5E3F0';

  const dismiss = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
    setShow(false);
    setShowIosInstructions(false);
  }, []);

  useEffect(() => {
    if (isStandalone() || !isMobile() || wasRecentlyDismissed()) return;

    // Android: catch beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Delay the banner so it doesn't annoy
    const timer = setTimeout(() => setShow(true), DELAY_BEFORE_SHOW);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        dismiss();
      } else {
        dismiss();
      }
      setDeferredPrompt(null);
    } else if (isIOS()) {
      setShowIosInstructions(true);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed bottom-20 left-3 right-3 sm:hidden z-[9998] rounded-2xl p-3 flex items-center gap-3"
        style={{ background: cardBg, border: `1px solid ${bd}`, boxShadow: '0 8px 30px rgba(0,0,0,0.15)', animation: 'slideUpFade 0.4s ease-out' }}
        data-testid="pwa-install-banner">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: V }}>
          <Download className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" style={{ color: tp }}>Installer l'app KadmiAi</p>
          <p className="text-[11px]" style={{ color: ts }}>Accès rapide depuis votre écran d'accueil</p>
        </div>
        <button
          onClick={handleInstall}
          className="px-3 py-2 rounded-lg text-xs font-semibold text-white shrink-0 transition-transform hover:scale-105"
          style={{ background: V }}
          data-testid="pwa-install-btn"
        >
          Installer
        </button>
        <button
          onClick={dismiss}
          className="p-1.5 rounded-lg shrink-0"
          style={{ color: ts }}
          aria-label="Fermer"
          data-testid="pwa-install-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* iOS instructions modal (Safari doesn't support beforeinstallprompt) */}
      {showIosInstructions && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={dismiss} data-testid="pwa-ios-modal">
          <div className="w-full max-w-md rounded-2xl p-5" style={{ background: cardBg, border: `1px solid ${bd}` }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: V }}>
                <Smartphone className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-base" style={{ color: tp }}>Installer sur iPhone</p>
                <p className="text-xs mt-0.5" style={{ color: ts }}>En 3 étapes via Safari</p>
              </div>
              <button onClick={dismiss} className="p-1 rounded-lg" style={{ color: ts }}><X className="h-4 w-4" /></button>
            </div>
            <ol className="space-y-2.5 mb-4">
              {[
                { n: 1, t: "Appuyez sur l'icône 'Partager'", s: "L'icône carrée avec une flèche vers le haut, en bas de l'écran Safari." },
                { n: 2, t: "Faites défiler et choisissez 'Sur l'écran d'accueil'", s: "Icône +" },
                { n: 3, t: "Appuyez sur 'Ajouter'", s: "L'icône KadmiAi apparaît sur votre écran d'accueil." }
              ].map(step => (
                <li key={step.n} className="flex gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 text-white" style={{ background: V }}>{step.n}</div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: tp }}>{step.t}</p>
                    <p className="text-xs" style={{ color: ts }}>{step.s}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Link to="/installer" onClick={dismiss}>
              <button className="w-full py-3 rounded-xl text-sm font-semibold text-white" style={{ background: V }}>
                Voir le guide complet
              </button>
            </Link>
          </div>
        </div>
      )}

      <style>{`@keyframes slideUpFade{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </>
  );
};

export default PwaInstallBanner;
